import { makeStyles } from 'tss-react/mui';
import get from 'lodash/get';

import { Tokens } from '../Theme';
import { BoxColors } from '../Box';

import {
  ChipSize,
  ChipColor,
  SizeScheme,
  ColorScheme,
} from './ColoredChip.types';

const getWidth = (width?: string | number, px?: (px: number) => number) => {
  if (!width) return 'fit-content';
  if (typeof width === 'number' && px) return px(width);
  return width;
};

const commonProps: (
  tokens: Tokens,
  px: (px: number) => number,
) => Pick<
  SizeScheme[keyof SizeScheme],
  'font' | 'borderRadius' | 'icon' | 'counter'
> = (tokens, px) => ({
  font: {
    size: tokens.typography.variant.body3.fontSize,
    lineHeight: tokens.typography.variant.body3.lineHeight,
  },
  borderRadius: tokens.radius.m,
  icon: {
    fontSize: px(14), // TODO - fix нет в токенах
    width: tokens.spacing['20'],
    height: tokens.spacing['20'],
    marginRight: tokens.spacing['10'],
    marginLeft: tokens.spacing['10'],
  },
  counter: {
    marginLeft: tokens.spacing['10'],
  },
});

const sizeScheme: (tokens: Tokens, px: (px: number) => number) => SizeScheme = (
  tokens,
  px,
) => ({
  [ChipSize.large]: {
    ...commonProps(tokens, px),
    height: px(48),
    padding: `14px ${tokens.spacing['16']}px`, // TODO - fix нет в токенах
  },
  [ChipSize.medium]: {
    ...commonProps(tokens, px),
    height: px(40),
    padding: `${tokens.spacing['10']}px ${tokens.spacing['16']}px`,
    icon: {
      ...commonProps(tokens, px).icon,
      marginRight: tokens.spacing['8'],
      marginLeft: tokens.spacing['8'],
    },
    counter: {
      marginLeft: tokens.spacing['8'],
    },
  },
  [ChipSize.small]: {
    ...commonProps(tokens, px),
    height: px(32),
    padding: `6px ${tokens.spacing['12']}px`, // TODO - fix нет в токенах
    icon: {
      ...commonProps(tokens, px).icon,
      marginRight: tokens.spacing['8'],
      marginLeft: tokens.spacing['8'],
    },
    counter: {
      marginLeft: tokens.spacing['8'],
    },
  },
  [ChipSize.extraSmall]: {
    ...commonProps(tokens, px),
    height: px(24),
    borderRadius: tokens.radius.s,
    icon: {
      fontSize: tokens.spacing['12'],
      width: tokens.spacing['16'],
      height: tokens.spacing['16'],
      marginRight: tokens.spacing['4'],
      marginLeft: tokens.spacing['4'],
    },
    padding: `${tokens.spacing['4']}px ${tokens.spacing['8']}px`,
    font: {
      size: tokens.typography.variant.caption.fontSize,
      lineHeight: tokens.typography.variant.caption.lineHeight,
    },
    counter: {
      marginLeft: tokens.spacing['4'],
    },
  },
});

export const colorScheme: ColorScheme = {
  [ChipColor.gray]: {
    color: (p) => p.text.primary,
    bgColor: (p) => p.background.greyscalePrimary,
    counterBgColor: 'gray',
  },
  [ChipColor.darkgray]: {
    color: (p) => p.text.tertiary,
    bgColor: (p) => p.background.greyscaleTertiary,
    counterBgColor: 'gray',
  },
  [ChipColor.green]: {
    color: (p) => p.success.main,
    bgColor: (p) => p.success.light,
    counterBgColor: 'lightGreen',
  },
  [ChipColor.orange]: {
    color: (p) => p.warning.main,
    bgColor: (p) => p.warning.light,
    counterBgColor: 'lightOrange',
  },
  [ChipColor.red]: {
    color: (p) => p.error.main,
    bgColor: (p) => p.error.light,
    counterBgColor: 'lightRed',
  },
  [ChipColor.blue]: {
    color: (p) => p.blue.main,
    bgColor: (p) => p.blue.light,
    counterBgColor: 'lightBlue',
  },
  [ChipColor.purple]: {
    color: (p) => p.review.main,
    bgColor: (p) => p.review.light,
    counterBgColor: 'lightPurple',
  },
  [ChipColor.white]: {
    color: (p) => p.background.lightPrimary,
    bgColor: (p) => p.background.lightPrimary,
    counterBgColor: 'white',
  },
};

export const useStyles = makeStyles<{
  size: ChipSize;
  fullWidth: boolean;
  color: ChipColor;
  borderColor?: BoxColors;
  disabled: boolean;
  width?: string | number;
  isBgWhite?: boolean;
}>()((
  { palette, tokens, px },
  { size, fullWidth, color, borderColor, disabled, width, isBgWhite },
) => {
  return {
    container: {
      color: !disabled
        ? colorScheme[color].color(palette)
        : tokens.colors.text.light.tertirary,
      position: 'relative',
      width: getWidth(width, px),
    },
    chip: {
      height: sizeScheme(tokens, px)[size].height,
      width: fullWidth ? '100%' : 'fit-content',
      borderRadius: sizeScheme(tokens, px)[size].borderRadius,
      ...(borderColor && {
        borderColor: get(tokens.colors, borderColor),
      }),
      padding: sizeScheme(tokens, px)[size].padding,
      fontSize: sizeScheme(tokens, px)[size].font.size,
      lineHeight: sizeScheme(tokens, px)[size].font.lineHeight,
      ...(isBgWhite && {
        backgroundColor: tokens.colors.bg.light.primary,
      }),
      ...(!isBgWhite && {
        backgroundColor: !disabled
          ? colorScheme[color].bgColor(palette)
          : tokens.colors.bg.greyscale.secondary,
      }),
      '& .MuiSvgIcon-root': {
        margin: 0,
        color: !disabled
          ? colorScheme[color].color(palette)
          : tokens.colors.text.light.tertirary,
      },
      '& .MuiChip-label': {
        padding: 0,
        overflow: 'visible',
      },
      '& .MuiChip-label .MuiTypography-root': {
        padding: 0,
        fontWeight: tokens.typography.variant.body3.fontWeight,
        fontSize: sizeScheme(tokens, px)[size].font.size,
        lineHeight: sizeScheme(tokens, px)[size].font.lineHeight,
        color: !disabled
          ? colorScheme[color].color(palette)
          : tokens.colors.text.light.tertirary,
      },
    },
    chipLabel: {
      display: 'flex',
      alignItems: 'center',
      color: !disabled
        ? colorScheme[color].color(palette)
        : tokens.colors.text.light.tertirary,
    },
    chipLabelTypography: {
      color: `${
        !disabled
          ? colorScheme[color].color(palette)
          : tokens.colors.text.light.tertirary
      } !important`,
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
    },
    startIcon: {
      marginRight: sizeScheme(tokens, px)[size].icon.marginRight,
    },
    endIcon: {
      marginLeft: sizeScheme(tokens, px)[size].icon.marginLeft,
    },
    counter: {
      marginLeft: sizeScheme(tokens, px)[size].counter.marginLeft,
    },
    counterSticked: {
      position: 'absolute',
      top: -tokens.spacing['8'],
      right: 0,
    },
    linkChip: {
      cursor: 'pointer',
    },
  };
});
